import Breadcrumbs from '@/components/Breadcrumbs';
import EmptyState from '@/components/common/EmptyState';
import AmaDescriptionBanner from '@/components/course/AmaDescriptionBanner';
import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import { useThemeColors } from '@/hooks/useThemeColors';
import AppLayout from '@/layouts/AppLayout';
import { AmaSeries, CourseData, SectionData } from '@/types';
import { Link } from '@inertiajs/react';
import { ChevronDown, ChevronRight, Play, Video } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    course: CourseData;
    section: SectionData | null;
    ama_series: AmaSeries[];
    ama_description: {
        bunny_url: string | null;
        description: string | null;
    } | null;
};

interface AmaSeriesCardProps {
    series: AmaSeries;
    courseSlug: string;
}

const AmaSeriesCard: React.FC<AmaSeriesCardProps> = ({
    series,
    courseSlug,
}) => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <GlassCard className="overflow-hidden shadow-lg hover:border-[var(--color-theme-primary)]">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex w-full cursor-pointer items-center justify-between px-6 py-4 text-left"
            >
                <div className="flex items-center space-x-4">
                    <div
                        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl p-2"
                        style={{
                            boxShadow:
                                'inset 0 3px 8px rgba(0,0,0,0.22), inset 0 1px 4px rgba(0,0,0,0.15)',
                        }}
                    >
                        <Video size={20} className={colors.iconColor} />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                            {series.title}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-neutral-400">
                            {series.record_count || 0}{' '}
                            {t('course.sessions', 'sesiones')}
                        </p>
                    </div>
                </div>
                {isExpanded ? (
                    <ChevronDown
                        size={20}
                        className="text-gray-400 dark:text-neutral-500"
                    />
                ) : (
                    <ChevronRight
                        size={20}
                        className="text-gray-400 dark:text-neutral-500"
                    />
                )}
            </button>

            {isExpanded && series.records && series.records.length > 0 && (
                <div className="border-t border-white/40 px-6 py-3 dark:border-white/10">
                    <div className="space-y-2">
                        {series.records.map((record) => (
                            <Link
                                key={record.id}
                                href={`/courses/${courseSlug}/ama-series/${series.id}/records/${record.id}/`}
                                className="group flex items-center justify-between rounded-lg px-4 py-3 transition-all duration-150 hover:bg-white/40 dark:hover:bg-white/10"
                            >
                                <div className="flex items-center space-x-3">
                                    <Play
                                        size={16}
                                        className="text-gray-400 group-hover:text-gray-600 dark:text-neutral-500"
                                    />
                                    <span className="font-medium text-gray-900 group-hover:text-gray-700 dark:text-white">
                                        {record.title}
                                    </span>
                                </div>
                                <ChevronRight
                                    size={16}
                                    className={`text-gray-400 transition-colors dark:text-neutral-500 group-hover:${colors.iconColor}`}
                                />
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {isExpanded && (!series.records || series.records.length === 0) && (
                <div className="border-t border-white/40 px-6 py-4 dark:border-white/10">
                    <p className="text-center text-sm text-gray-500 dark:text-neutral-400">
                        {t(
                            'course.noRecordsAvailable',
                            'No hay grabaciones disponibles'
                        )}
                    </p>
                </div>
            )}
        </GlassCard>
    );
};

const AmaSeriesListContent: React.FC<Props> = ({
    course,
    section,
    ama_series,
    ama_description,
}) => {
    const { t } = useTranslation();
    const { route } = useRoutes();
    const pageTitle = `Q&A - ${course.title}`;

    const breadcrumbItems = [
        {
            label: t('course.sectionDetail.courses'),
            href: route('home').toString(),
        },
        {
            label: course.title,
            href: `/courses/${course.slug}/`,
        },
        { label: t('course.amaRecordedSessions', 'Sesiones AMA grabadas') },
    ];

    return (
        <AppLayout>
            <PageTitle title={pageTitle} />
            <div className="min-h-screen">
                <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
                    {/* Breadcrumbs */}
                    <div className="mb-6">
                        <Breadcrumbs items={breadcrumbItems} />
                    </div>

                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                            {t(
                                'course.amaRecordedSessions',
                                'Sesiones AMA grabadas'
                            )}
                        </h1>
                        {section && (
                            <p className="mt-2 text-gray-600 dark:text-neutral-400">
                                {t(
                                    'course.filteringBySection',
                                    'Filtrando por sección'
                                )}
                                : {section.title}
                            </p>
                        )}
                    </div>

                    {/* AMA Description (global) */}
                    {ama_description && (
                        <AmaDescriptionBanner
                            amaDescription={ama_description}
                        />
                    )}

                    {/* Series List */}
                    {ama_series.length === 0 ? (
                        <EmptyState
                            icon={Video}
                            title={t(
                                'course.noAmaSeriesAvailable',
                                'No hay series AMA disponibles'
                            )}
                            description={t(
                                'course.noAmaSeriesDescription',
                                'Las sesiones AMA grabadas aparecerán aquí cuando estén disponibles.'
                            )}
                        />
                    ) : (
                        <div className="space-y-4">
                            {ama_series.map((series) => (
                                <AmaSeriesCard
                                    key={series.id}
                                    series={series}
                                    courseSlug={course.slug}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </AppLayout>
    );
};

const AmaSeriesList: React.FC<Props> = (props) => {
    return (
        <ThemeWrapper>
            <AmaSeriesListContent {...props} />
        </ThemeWrapper>
    );
};

export default AmaSeriesList;
